import {
	Box,
	Button,
	Text,
	Icons,
	Stack,
	Table,
	Container,
	Heading,
	Col,
	Grid,
	joinAttributes,
	Spinner,
	TabContent,
	TabItem,
	Tabs,
	TabsList,
	Time,
	Money,
	Inline,
	MoneySum,
	TimeDuration,
	Badge,
} from "@sembark-travel/ui-base"
import {
	useSearch,
	TSearchParams,
	Search,
	ListView,
	areAdvancedFiltersAppliedDefault,
} from "@sembark-travel/ui-list"
import {
	Breadcrumbs,
	useLocationQuery,
	NavLink,
	ButtonLink,
	Link,
	useGetBackUrlFromLocation,
} from "@sembark-travel/ui-router"
import {
	dateToQuery,
	dateToUTCString,
	endOf,
	formatDate,
	getDiff,
	localOrUtcTimestampToLocalDate,
	parseDateFromQuery,
	startOf,
	subtractUnit,
	utcTimestampToLocalDateString,
} from "@sembark-travel/datetime-utils"
import { isNumeric, numberToLocalString } from "@sembark-travel/number-utils"
import { IListResponse, useXHR, XHRInstance } from "@sembark-travel/xhr"
import { useEffect, useMemo } from "react"
import useSWR from "swr"
import { AccountsList } from "../Accounting"
import { ICabSchedule, IScheduledCab } from "../CabScheduling"
import config from "../config"
import {
	ContactsList,
	Email,
	IContact,
	PhoneNumber,
	SelectContact,
} from "../Contacts"
import { DriversList, TDriver } from "../Drivers"
import { PaymentsList } from "../Payments"
import { generatePath, Path } from "../router-utils"
import { ISupplier, ITSPTripWiseCabsSchedule, useSupplier } from "./store"
import {
	AddPaymentPreferenceInDialog,
	UpdateItemInDialog,
	UpdatePaymentPreferenceInDialog,
} from "./UpdateItem"
import {
	DatePickerField,
	DateRangePickerField,
	SelectField,
	SelectInputField,
	useFieldValue,
	useForm,
} from "@sembark-travel/ui-form"
import { PERMISSIONS, useCheckPermissions } from "../Auth"
import { TTravelActiviytBooking } from "../TravelActivityBookings"
import { getStartDateTimeOfCabSchedule } from "../CabScheduling/utils"
import { Dialog, useDialog } from "@sembark-travel/ui-dialog"
import { TemporaryStorage } from "../DownloadManager"
import {
	addMoneyFromDifferentCurrencies,
	moneyParseByDecimal,
	formatMoneyByIntl,
	TMoney,
} from "@sembark-travel/money"
import { ICab } from "../Cabs"
import { ITrip } from "../Trips"
import { getDisplayNameForStage, IBooking } from "../HotelBookings"
import { IMealPlan } from "../MealPlans"
import { SelectHotelBookingPaymentStatuses } from "../HotelBookings/List"
import { TVisaBooking, TBookingState } from "../VisaBookings"
import { getTypeOfVisaDisplayName } from "../Visa"

export function SupplierXHR(xhr: XHRInstance) {
	return {
		async getSupplierById(id: string): Promise<ISupplier> {
			return xhr
				.get(`/transport-service-providers/${id}`, {
					params: {
						include: "booking_exists",
					},
				})
				.then((resp) => resp.data.data)
		},
		async getBookings(params: unknown): Promise<IListResponse<IScheduledCab>> {
			return xhr.get(`/scheduled-cabs`, { params }).then((resp) => resp.data)
		},
		async getTripWiseBookings(
			params: unknown
		): Promise<IListResponse<ITSPTripWiseCabsSchedule>> {
			return xhr
				.get(`/trip-wise-cab-schedule`, { params })
				.then((resp) => resp.data)
		},
		async getTravelActivityBookings(
			params: unknown
		): Promise<IListResponse<TTravelActiviytBooking>> {
			return xhr
				.get(`/travel-activity-bookings`, { params })
				.then((resp) => resp.data)
		},
		async getHotelBookings(
			params: unknown
		): Promise<IListResponse<IBooking & { trip: ITrip }>> {
			return xhr.get(`/hotel-bookings`, { params }).then((resp) => resp.data)
		},
		async getVisaBookings(
			params: unknown
		): Promise<IListResponse<TVisaBooking & { trip: ITrip }>> {
			return xhr.get(`/visa-bookings`, { params }).then((resp) => resp.data)
		},
		async createContact(
			id: string | number,
			contactData: object
		): Promise<IContact> {
			return xhr
				.post(`/transport-service-provider-contacts`, {
					transport_service_provider_id: id,
					...contactData,
				})
				.then((resp) => resp.data.data)
		},
	}
}

function useTransportServiceProvider(
	transportServiceProviderId?: string,
	shouldFetch = false
) {
	const xhr = useXHR()
	const {
		data: transportServiceProvider,
		mutate: fetchTransportServiceProvider,
		error,
	} = useSWR(
		shouldFetch && transportServiceProviderId
			? `/api/transport-service-providers/${transportServiceProviderId}`
			: null,
		() => SupplierXHR(xhr).getSupplierById(transportServiceProviderId || "")
	)
	const isFetching = !transportServiceProvider && !error
	return {
		isFetching,
		transportServiceProvider,
		error,
		fetchTransportServiceProvider,
	}
}

function getSupplierListBackLink(supplier: ISupplier): Path {
	const {
		supplies_cabs,
		supplies_travel_activities,
		supplies_hotels,
		supplies_visas,
	} = supplier
	const typeMap: { check: boolean; path: Path }[] = [
		{ check: supplies_cabs, path: "/suppliers/transport" },
		{ check: supplies_travel_activities, path: "/suppliers/activity" },
		{ check: supplies_hotels, path: "/suppliers/hotel" },
		{ check: supplies_visas, path: "/suppliers/visa" },
	]
	const entry = typeMap.find(({ check }) => check)
	return entry ? entry.path : "/suppliers/transport"
}

export function SupplierDetails({
	supplierId,
	children,
}: Readonly<{
	supplierId: string
	children?: (props: {
		transportServiceProvider: ISupplier
		revalidate: () => void
	}) => React.ReactNode
}>) {
	const {
		transportServiceProvider,
		isFetching,
		fetchTransportServiceProvider,
	} = useTransportServiceProvider(supplierId.toString(), true)
	const { hasPermission } = useCheckPermissions()
	const back = useGetBackUrlFromLocation()
	if (isFetching && !transportServiceProvider)
		return <Spinner alignCenter padding="4" />
	if (!transportServiceProvider)
		return <Box>Transport service provider does not exists</Box>
	const { name } = transportServiceProvider
	const {
		cab_bookings,
		travel_activity_bookings,
		hotel_bookings,
		visa_bookings,
	} = transportServiceProvider.booking_exists || {}

	return (
		<Box>
			<Breadcrumbs
				title={name}
				items={[
					[
						back ??
							generatePath(getSupplierListBackLink(transportServiceProvider)),
						"Suppliers",
					],
				]}
			/>
			<Box>
				<Tabs layout="col" gap="0">
					<TabsList>
						<NavLink
							to={generatePath("/suppliers/:supplierId", {
								supplierId: supplierId,
							})}
							end
						>
							Details
						</NavLink>
						{hasPermission(PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS) ? (
							<>
								{transportServiceProvider.supplies_cabs || cab_bookings ? (
									<NavLink
										to={generatePath(
											"/suppliers/:supplierId/bookings/cab-schedules",
											{
												supplierId: supplierId,
											}
										)}
									>
										Cab Bookings
									</NavLink>
								) : null}
								{transportServiceProvider.supplies_travel_activities ||
								travel_activity_bookings ? (
									<NavLink
										to={generatePath(
											"/suppliers/:supplierId/travel-activity-bookings",
											{
												supplierId: supplierId,
											}
										)}
									>
										Activity Bookings
									</NavLink>
								) : null}
							</>
						) : null}
						{(hasPermission(PERMISSIONS.VIEW_HOTEL_SUPPLIERS) &&
							transportServiceProvider.supplies_hotels) ||
						hotel_bookings ? (
							<NavLink
								to={generatePath("/suppliers/:supplierId/hotels-bookings", {
									supplierId: supplierId,
								})}
							>
								Hotel Bookings
							</NavLink>
						) : null}
						{hasPermission(PERMISSIONS.VIEW_VISA_BOOKINGS) ? (
							<>
								{transportServiceProvider.supplies_visas || visa_bookings ? (
									<NavLink
										to={generatePath("/suppliers/:supplierId/visas-bookings", {
											supplierId: supplierId,
										})}
									>
										Visa Bookings
									</NavLink>
								) : null}
							</>
						) : null}
						<NavLink
							to={generatePath("/suppliers/:supplierId/payments/outgoing", {
								supplierId,
							})}
						>
							Payments
						</NavLink>
						{hasPermission(PERMISSIONS.MANAGE_ACCOUNTING) ? (
							<NavLink
								to={generatePath("/suppliers/:supplierId/accounting", {
									supplierId,
								})}
							>
								Accounting
							</NavLink>
						) : null}
						<NavLink
							to={generatePath("/suppliers/:supplierId/activities", {
								supplierId,
							})}
						>
							Activities
						</NavLink>
					</TabsList>
					<TabContent>
						{children
							? children({
									transportServiceProvider,
									revalidate: fetchTransportServiceProvider,
								})
							: null}
					</TabContent>
				</Tabs>
			</Box>
		</Box>
	)
}

export function SupplierBasicDetails({
	supplier,
	revalidate,
}: Readonly<{
	supplier: ISupplier
	revalidate: () => void
}>) {
	const { hasAnyPermission } = useCheckPermissions()
	const {
		contacts,
		drivers,
		name,
		solo_driver,
		supplies_cabs,
		supplies_travel_activities,
		supplies_hotels,
		supplies_visas,
		hotel_supplier_payment_preference,
		activity_supplier_payment_preference,
		transport_supplier_payment_preference,
		visa_countries,
	} = supplier
	const xhr = useXHR()
	const isPaymentPreferencePresent = useMemo(
		() =>
			Boolean(
				hotel_supplier_payment_preference ||
				activity_supplier_payment_preference ||
				transport_supplier_payment_preference
			),
		[
			hotel_supplier_payment_preference,
			activity_supplier_payment_preference,
			transport_supplier_payment_preference,
		]
	)
	return (
		<Box>
			<Container paddingY="4" bgColor="default" borderBottomWidth="1">
				<Inline justifyContent="between" flexWrap="wrap" gap="4">
					<Stack gap="2" paddingTop="4">
						<Heading as="h2" fontSize="xl">
							{name}
						</Heading>
						{isPaymentPreferencePresent ? (
							<Stack gap="1">
								<Heading as="h3" fontSize="md">
									Payment Preferences
								</Heading>
								{solo_driver ? (
									<Text as="span" color="muted">
										<Icons.Driver title="Single Driver" /> Solo Driver
									</Text>
								) : null}
								{supplies_cabs ? (
									<Text as="span" color="muted">
										<Icons.Taxi title="Cab Supplier" /> Cabs :{" "}
										{supplier.transport_supplier_payment_preference
											? supplier.transport_supplier_payment_preference.name
											: "N/A"}
									</Text>
								) : null}
								{supplies_travel_activities ? (
									<Text as="span" color="muted">
										<Icons.Ticket title="Travel Activity Supplier" />{" "}
										TravelActivities :{" "}
										{supplier.activity_supplier_payment_preference
											? supplier.activity_supplier_payment_preference.name
											: "N/A"}
									</Text>
								) : null}
								{supplies_hotels ? (
									<Text as="span" color="muted">
										<Icons.Bed title="Hotel Supplier" /> Hotel :{" "}
										{supplier.hotel_supplier_payment_preference
											? supplier.hotel_supplier_payment_preference.name
											: "N/A"}
									</Text>
								) : null}
								{supplies_visas ? (
									<Text as="span" color="muted">
										<Icons.Visa title="Visa Supplier" /> Visa
									</Text>
								) : null}
							</Stack>
						) : (
							<Inline as="span" color="muted">
								{joinAttributes(
									solo_driver ? (
										<Inline gap="1" justifyContent="center" alignItems="center">
											<Icons.Driver title="Single Driver" />
											<Text>Solo Driver</Text>
										</Inline>
									) : null,
									supplies_cabs ? (
										<Inline gap="1" justifyContent="center" alignItems="center">
											<Icons.Taxi title="Cab Supplier" />
											<Text>Cabs</Text>
										</Inline>
									) : null,
									supplies_travel_activities ? (
										<Inline gap="1" justifyContent="center" alignItems="center">
											<Icons.Ticket title="Travel Activity Supplier" />
											<Text>Travel Activities</Text>
										</Inline>
									) : null,
									supplies_hotels ? (
										<Inline gap="1" justifyContent="center" alignItems="center">
											<Icons.Bed title="Hotel Supplier" />
											<Text>Hotel</Text>
										</Inline>
									) : null,
									supplies_visas ? (
										<Inline gap="1" justifyContent="center" alignItems="center">
											<Icons.Visa title="Visa Supplier" />
											<Text>Visa</Text>
										</Inline>
									) : null
								)}
							</Inline>
						)}
						{supplies_visas ? (
							<Inline gap="1">
								<Text color="muted">Country:</Text>
								<Text as="span">
									{Array.isArray(visa_countries) && visa_countries.length > 0
										? visa_countries.map((c) => c.name).join(", ")
										: "N/A"}
								</Text>
							</Inline>
						) : null}
					</Stack>
					<Inline gap="2" flexWrap="wrap">
						{hasAnyPermission(
							PERMISSIONS.VIEW_HOTEL_SUPPLIERS,
							PERMISSIONS.VIEW_TRANSPORT_SERVICE_PROVIDERS
						) ? (
							<>
								{isPaymentPreferencePresent ? (
									<UpdatePaymentPreferenceInDialog
										supplier={supplier}
										onSuccess={() => revalidate()}
									>
										{({ open }) => (
											<Button onClick={() => open()} size="sm">
												<Icons.Pencil /> Edit Payment Preference
											</Button>
										)}
									</UpdatePaymentPreferenceInDialog>
								) : (
									<AddPaymentPreferenceInDialog
										supplier={supplier}
										onSuccess={() => revalidate()}
									>
										{({ open }) => (
											<Button onClick={() => open()} size="sm">
												<Icons.Plus /> Add Payment Preference
											</Button>
										)}
									</AddPaymentPreferenceInDialog>
								)}
							</>
						) : null}
						<UpdateItemInDialog
							supplier={supplier}
							onSuccess={() => revalidate()}
						>
							{({ edit }) => (
								<Button onClick={() => edit()} size="sm" align="left">
									<Icons.Pencil /> Edit
								</Button>
							)}
						</UpdateItemInDialog>
					</Inline>
				</Inline>
			</Container>
			<Box
				paddingY="8"
				paddingX="4"
				bgColor="default"
				roundedBottom="lg"
				borderLeftWidth="1"
				borderRightWidth="1"
				borderBottomWidth="1"
			>
				<Grid gap="6" paddingTop={"4"}>
					<Col sm={12} md={6}>
						<ContactsList
							contacts={contacts}
							phoneNumberRequired
							onCreate={async (data) => {
								return SupplierXHR(xhr)
									.createContact(supplier.id, data)
									.then((contact) => {
										revalidate()
										return contact
									})
							}}
							onChange={() => revalidate()}
						/>
					</Col>
					<Col>
						{supplies_cabs ? (
							<DriversList
								supplier={supplier}
								drivers={drivers}
								onChange={() => revalidate()}
							/>
						) : null}
					</Col>
				</Grid>
			</Box>
		</Box>
	)
}

type TBookingStatus = "all" | "booked" | "assigned_but_not_booked"

type TFilters = TSearchParams & {
	pickup_after?: Date
	pickup_before?: Date
	trips_start_date_after?: Date
	trips_start_date_before?: Date
	status?: TBookingStatus
	drivers?: Array<TDriver>

	report_type?: "overall_checkin" | "overall_checkout"
	overall_checkin_after?: string
	overall_checkin_before?: string
	overall_checkout_after?: string
	overall_checkout_before?: string
	payment_statuses?: Array<{ id: string; name: string }>
	report_stage?: "all" | "past" | "upcoming"
	visa_stage?: TBookingState | "all"
	visa_start_date?: Date
	visa_end_date?: Date
}

type TFiltersInLocationUrl = TSearchParams & {
	pickup_after?: string
	pickup_before?: string
	trips_start_date_after?: string
	trips_start_date_before?: string
	status?: TBookingStatus
	drivers?: Array<string>

	report_type?: "overall_checkin" | "overall_checkout"
	overall_checkin_after?: string
	overall_checkin_before?: string
	overall_checkout_after?: string
	overall_checkout_before?: string
	payment_statuses?: Array<string>
	report_stage?: "all" | "past" | "upcoming"
	visa_stage?: TBookingState | "all"
	visa_start_date?: string
	visa_end_date?: string
}

function filtersToUrl(filters: TFilters): TFiltersInLocationUrl {
	const {
		pickup_after,
		pickup_before,
		page,
		q,
		status,
		drivers,
		trips_start_date_after,
		trips_start_date_before,
		report_type,
		overall_checkin_after,
		overall_checkin_before,
		overall_checkout_after,
		overall_checkout_before,
		payment_statuses,
		report_stage = "upcoming",
		visa_stage,
		visa_start_date,
		visa_end_date,
	} = filters
	const filtersInLocation: TFiltersInLocationUrl = {}
	if (q) {
		filtersInLocation.q = q
	}
	if (status) {
		filtersInLocation.status = status
	}
	if (report_stage) {
		filtersInLocation.report_stage = report_stage
	}
	if (page) {
		filtersInLocation.page = page
	}
	if (pickup_after) {
		filtersInLocation.pickup_after = dateToQuery(pickup_after)
	}
	if (pickup_before) {
		filtersInLocation.pickup_before = dateToQuery(pickup_before)
	}
	if (trips_start_date_after) {
		filtersInLocation.trips_start_date_after = dateToQuery(
			trips_start_date_after
		)
	}
	if (trips_start_date_before) {
		filtersInLocation.trips_start_date_before = dateToQuery(
			trips_start_date_before
		)
	}
	if (report_type) {
		filtersInLocation.report_type = report_type
	}
	if (report_type === "overall_checkin") {
		if (overall_checkin_after) {
			filtersInLocation.overall_checkin_after = dateToQuery(
				overall_checkin_after
			)
		}
		if (overall_checkin_before) {
			filtersInLocation.overall_checkin_before = dateToQuery(
				overall_checkin_before
			)
		}
	} else {
		if (overall_checkin_after) {
			filtersInLocation.overall_checkin_after = dateToQuery(
				overall_checkin_after
			)
		}
		if (overall_checkin_before) {
			filtersInLocation.overall_checkin_before = dateToQuery(
				overall_checkin_before
			)
		}
	}

	if (overall_checkout_after) {
		filtersInLocation.overall_checkout_after = dateToQuery(
			overall_checkout_after
		)
	}
	if (overall_checkout_before) {
		filtersInLocation.overall_checkout_before = dateToQuery(
			overall_checkout_before
		)
	}
	if (drivers) {
		filtersInLocation.drivers = drivers.map((d) => `${d.id}#${d.name}`)
	}
	if (payment_statuses && payment_statuses.length)
		filtersInLocation.payment_statuses = payment_statuses.map(
			(p) => `${p.id}#${p.name}`
		)

	if (visa_stage) {
		filtersInLocation.visa_stage = visa_stage
	}

	if (visa_start_date) {
		filtersInLocation.visa_start_date = dateToQuery(visa_start_date)
	}
	if (visa_end_date) {
		filtersInLocation.visa_end_date = dateToQuery(visa_end_date)
	}
	return filtersInLocation
}

function filtersFromUrl(filters: TFiltersInLocationUrl): TFilters {
	const {
		pickup_after,
		pickup_before,
		q,
		page,
		status,
		drivers,
		trips_start_date_after,
		trips_start_date_before,
		overall_checkin_after,
		overall_checkin_before,
		overall_checkout_after,
		overall_checkout_before,
		report_type,
		report_stage,
		visa_stage,
		visa_start_date,
		visa_end_date,
	} = filters
	const params: TFilters = {}
	if (q) {
		params.q = q
	}
	if (page) {
		params.page = page
	}
	if (status) {
		params.status = status
	}
	if (report_stage) {
		params.report_stage = report_stage
	}
	if (pickup_after) {
		params.pickup_after = parseDateFromQuery(pickup_after)
	}
	if (pickup_before) {
		params.pickup_before = parseDateFromQuery(pickup_before)
	}
	if (trips_start_date_after) {
		params.trips_start_date_after = parseDateFromQuery(trips_start_date_after)
	}
	if (trips_start_date_before) {
		params.trips_start_date_before = parseDateFromQuery(trips_start_date_before)
	}
	if (report_type) {
		params.report_type = report_type
	}
	if (overall_checkin_after) {
		params.overall_checkin_after = overall_checkin_after
	}
	if (overall_checkin_before) {
		params.overall_checkin_before = overall_checkin_before
	}
	if (overall_checkout_after) {
		params.overall_checkout_after = overall_checkout_after
	}
	if (overall_checkout_before) {
		params.overall_checkout_before = overall_checkout_before
	}
	if (drivers) {
		params.drivers = drivers.map((d) => {
			const [id, ...name] = d.split("#")
			return {
				id,
				name: name.join("#"),
			} as never as TDriver
		})
	}
	if (filters.payment_statuses) {
		params.payment_statuses = filters.payment_statuses.map((p) => {
			const [id, ...name] = p.split("#")
			return { id, name: name.join("#") }
		})
	}
	if (visa_stage) {
		params.visa_stage = visa_stage
	}

	if (visa_start_date) {
		params.visa_start_date = parseDateFromQuery(visa_start_date)
	}
	if (visa_end_date) {
		params.visa_end_date = parseDateFromQuery(visa_end_date)
	}
	return params
}

function filtersToRequestParams(filters: TFilters) {
	const {
		pickup_after,
		pickup_before,
		page,
		q,
		status,
		drivers,
		trips_start_date_after,
		trips_start_date_before,
		visa_stage,
		visa_start_date,
		visa_end_date,
	} = filters
	const params: {
		status?: string
		page?: number
		q?: string
		pickup_after?: string
		pickup_before?: string
		trips_start_date_after?: string
		trips_start_date_after_local?: string
		trips_start_date_before?: string
		trips_start_date_before_local?: string
		start_date?: string
		start_date_local?: string
		end_date?: string
		end_date_local?: string
		booked?: 0 | 1
		drivers?: Array<number>
		overall_checkin_after?: string
		overall_checkin_after_local?: string
		overall_checkin_before?: string
		overall_checkin_before_local?: string
		overall_checkout_after?: string
		overall_checkout_after_local?: string
		overall_checkout_before?: string
		overall_checkout_before_local?: string
		sort?: string
		payment_statuses?: Array<string>
		report_stage?: "all" | "past" | "upcoming"
		stages?: Array<TBookingState>
		visa_end_date?: string
		visa_start_date?: string
	} = { status }
	if (page) {
		params.page = page
	}
	if (q) {
		params.q = q
	}
	if (status) {
		params.status = status
	}
	if (pickup_after) {
		params.pickup_after = dateToUTCString(pickup_after)
		params.start_date = dateToUTCString(pickup_after)
		params.start_date_local = formatDate(pickup_after, "YYYY-MM-DD")
	}
	if (pickup_before) {
		params.pickup_before = dateToUTCString(endOf(pickup_before, "day"))
		params.end_date = dateToUTCString(endOf(pickup_before, "day"))
		params.end_date_local = formatDate(
			endOf(pickup_before, "day"),
			"YYYY-MM-DD"
		)
	}
	if (trips_start_date_after) {
		params.trips_start_date_after = dateToUTCString(trips_start_date_after)
		params.trips_start_date_after_local = formatDate(
			trips_start_date_after,
			"YYYY-MM-DD"
		)
	}
	if (trips_start_date_before) {
		params.trips_start_date_before = dateToUTCString(
			endOf(trips_start_date_before, "day")
		)
		params.trips_start_date_before_local = formatDate(
			trips_start_date_before,
			"YYYY-MM-DD"
		)
	}
	if (filters.overall_checkin_after) {
		params.overall_checkin_after = dateToUTCString(
			filters.overall_checkin_after
		)
		params.overall_checkin_after_local = formatDate(
			filters.overall_checkin_after,
			"YYYY-MM-DD"
		)
	}
	if (filters.overall_checkin_before) {
		params.overall_checkin_before = dateToUTCString(
			filters.overall_checkin_before
		)

		params.overall_checkin_before_local = formatDate(
			filters.overall_checkin_before,
			"YYYY-MM-DD"
		)
	}
	if (filters.overall_checkout_after) {
		params.overall_checkout_after = dateToUTCString(
			filters.overall_checkout_after
		)
		params.overall_checkout_after_local = formatDate(
			filters.overall_checkout_after,
			"YYYY-MM-DD"
		)
	}
	if (filters.payment_statuses) {
		params.payment_statuses = (filters.payment_statuses || []).map((p) => p.id)
	}

	if (filters.overall_checkout_before) {
		params.overall_checkout_before = dateToUTCString(
			filters.overall_checkout_before
		)
		params.overall_checkout_before_local = formatDate(
			filters.overall_checkout_before,
			"YYYY-MM-DD"
		)
	}
	if (filters.report_type) {
		const reportType = filters.report_type
		params.sort =
			filters.report_stage !== "upcoming"
				? reportType
					? `-${reportType}`
					: "-overall_checkin"
				: reportType || "overall_checkin"
	}

	if (drivers?.length) {
		params.drivers = drivers.map((d) => d.id)
	}

	if (visa_stage && visa_stage !== "all") {
		params.stages = [visa_stage]
	}

	if (visa_start_date) {
		params.visa_start_date = dateToUTCString(visa_start_date)
	}

	if (visa_end_date) {
		params.visa_end_date = dateToUTCString(visa_end_date)
	}

	return params
}

export function SupplierBookings({
	transportServiceProvider: supplier,
}: Readonly<{
	transportServiceProvider: ISupplier
}>) {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToUrl,
		fromQuery: filtersFromUrl,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
		status: query.status || "booked",
	})
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Box>
			<Search
				initialParams={params}
				placeholder="Search by Guest, Services etc..."
				title="Cab Schedules"
				resetParams={(params) => ({
					q: "",
					status: params.status,
				})}
				Filters={Filters}
				actions={
					<Inline gap={"2"}>
						<TemporaryStorage
							initUrl="/scheduled-cabs/export"
							initParams={{
								...filtersToRequestParams(params),
								transportServiceProviders: [supplier.id],
								timezone_offset: config.timezoneOffset,
							}}
						>
							{({ message, generate, url, reset, isGenerating }) =>
								url ? (
									<Button
										as="a"
										href={url}
										target="_blank"
										download
										level="secondary"
										onClick={() => reset()}
									>
										{message}
									</Button>
								) : (
									<Button
										onClick={generate}
										disabled={isGenerating}
										level="secondary"
									>
										{message || (
											<>
												<Icons.DocumentDownload />
											</>
										)}{" "}
										Excel
									</Button>
								)
							}
						</TemporaryStorage>
						<ButtonLink
							to={generatePath(
								`/suppliers/:supplierId/bookings/trip-wise-booking`,
								{ supplierId: supplier.id.toString() }
							)}
						>
							Trip Wise
						</ButtonLink>
					</Inline>
				}
				areAdvancedFiltersApplied={(filters) => {
					const { status, ...otherParams } = filters
					return Object.keys(otherParams).length > 0
				}}
				onSearch={(newParams) => {
					setParams({ ...newParams, page: 1 })
				}}
			>
				{({ searchParams, setSearchParams }) => (
					<Tabs layout="col" gap={"0"}>
						<TabsList>
							<TabItem
								active={searchParams.status === "booked"}
								onClick={() => {
									setSearchParams({ ...searchParams, status: "booked" })
								}}
							>
								Booked
							</TabItem>
							<TabItem
								active={searchParams.status === "assigned_but_not_booked"}
								onClick={() => {
									setSearchParams({
										...searchParams,
										status: "assigned_but_not_booked",
									})
								}}
							>
								Not Booked
							</TabItem>
							<TabItem
								active={searchParams.status === "all"}
								onClick={() => {
									setSearchParams({ ...searchParams, status: "all" })
								}}
							>
								All
							</TabItem>
						</TabsList>
						<TabContent>
							<ListView<IScheduledCab, TFilters>
								pageKey={`transport-service-providers/${supplier.id}/bookings`}
								fetch={(xhr, filters) =>
									SupplierXHR(xhr).getBookings({
										...filtersToRequestParams(filters),
										transportServiceProviders: [supplier.id],
									})
								}
								onPageChange={(page) => setParams({ ...params, page })}
								params={params}
							>
								{({ items }) => (
									<Table
										bordered
										hover
										responsive
										headers={[
											"Service",
											"Date",
											"Cab Type",
											"Cab",
											"Driver",
											"Guest",
											"Amount",
										]}
										alignCols={{ 6: "right" }}
										rows={items.map(
											({
												currency,
												booked,
												booked_price,
												cab_type,
												cab,
												driver,
												cab_schedule,
												trip,
												latest_booking_stage,
												payments_amount,
												paid_payments_amount,
												total_payments_amount,
												refunding_payments_amount,
												paid_refunding_payments_amount,
											}) => [
												cab_schedule ? (
													<Box>
														<Box>
															<Link
																color="accent"
																fontWeight="semibold"
																to={generatePath("/cab-schedules/:scheduleId", {
																	scheduleId: cab_schedule.id.toString(),
																})}
																anchored
															>
																{cab_schedule.transport_service.short_name}
															</Link>
														</Box>
														<Box fontSize="sm" color="muted">
															{cab_schedule.transport_service.service}
														</Box>
													</Box>
												) : null,
												cab_schedule ? (
													<Box>
														<Box>
															<Time
																timestamp={cab_schedule.start_date}
																localTimestamp={cab_schedule.start_date_local}
															/>
														</Box>
														<Box fontSize="sm" color="muted">
															{getStartDateTimeOfCabSchedule(cab_schedule) ? (
																<Time
																	value={getStartDateTimeOfCabSchedule(
																		cab_schedule
																	)}
																	format="HH:mm"
																/>
															) : null}
														</Box>
													</Box>
												) : null,
												<Box>
													<Box>{cab_type.name}</Box>
													<Box fontSize="sm" color="muted">
														{latest_booking_stage.title}
													</Box>
												</Box>,
												cab ? (
													<Box>
														<Box>{cab.name}</Box>
														<Box fontSize="sm" color="muted">
															{cab.number_plate}
														</Box>
													</Box>
												) : null,
												driver ? (
													<Box>
														<Box>{driver.name}</Box>
														<Box fontSize="sm" color="muted">
															{joinAttributes(
																driver.phone_number ? (
																	<PhoneNumber
																		value={driver.phone_number}
																		iconOnly
																	/>
																) : null,
																driver.email ? (
																	<Email value={driver.email} iconOnly />
																) : null
															)}
														</Box>
													</Box>
												) : null,
												trip ? (
													<Box>
														<Box>
															<Link
																to={generatePath("/trips/:tripId", {
																	tripId: trip.id.toString(),
																})}
																color="accent"
															>
																{trip.contact.name}
															</Link>
														</Box>
														<Box fontSize="sm" color="muted">
															{joinAttributes(
																trip.id,
																trip.contact.phone_numbers?.length ? (
																	<PhoneNumber
																		value={trip.contact.phone_numbers}
																		iconOnly
																	/>
																) : null,
																trip.contact.email ? (
																	<Email value={trip.contact.email} iconOnly />
																) : null
															)}
														</Box>
													</Box>
												) : null,
												booked_price !== undefined && booked_price !== null ? (
													<Box>
														<Box>
															{isNumeric(booked_price) ? (
																<Inline
																	fontWeight="semibold"
																	alignItems="center"
																	justifyContent="end"
																	gap="1"
																>
																	{refunding_payments_amount === null ? (
																		// If not in refund mode, show Green Check when
																		// - booked
																		// - or, when booking with no paid instalments is dropped without cancellation charges
																		// AND, when all payments are paid.
																		// In case payments_amount is null, paid_payments_amount must also be null.
																		booked &&
																		paid_payments_amount === payments_amount ? (
																			<Box display="inlineBlock" marginLeft="1">
																				<Icons.OkCircleSolid color="success" />
																			</Box>
																		) : null
																	) : paid_refunding_payments_amount ===
																	  refunding_payments_amount ? (
																		// If in refund mode, show Green Check when
																		// - Refund is complete
																		<Box display="inlineBlock" marginLeft="1">
																			<Icons.OkCircleSolid color="success" />
																		</Box>
																	) : null}
																	<Text>Booking:</Text>
																	{latest_booking_stage.is_same_or_after_booked ? (
																		<Money
																			showCurrency
																			amount={total_payments_amount || 0}
																			currency={currency}
																		/>
																	) : isNumeric(booked_price) ? (
																		<Box>
																			<Money
																				showCurrency
																				amount={booked_price}
																				currency={currency}
																			/>
																		</Box>
																	) : null}
																</Inline>
															) : null}
															{isNumeric(paid_payments_amount) ? (
																<Box
																	fontSize="sm"
																	fontWeight="semibold"
																	title={`${formatMoneyByIntl(moneyParseByDecimal(paid_payments_amount, currency))} Paid`}
																	marginTop="1"
																	color="success"
																>
																	Paid:{" "}
																	<Money
																		amount={paid_payments_amount}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
															isNumeric(payments_amount) &&
															  Number(payments_amount || 0) > 0 ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="muted"
																>
																	Paid:{" "}
																	<Money
																		amount={0}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
															{isNumeric(refunding_payments_amount) ? (
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="warning"
																	title="Refund"
																	whiteSpace="nowrap"
																>
																	Refund:{" "}
																	<Money
																		amount={paid_refunding_payments_amount || 0}
																		currency={currency}
																		showCurrency
																	/>
																	{" / "}
																	<Money
																		amount={refunding_payments_amount}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
														</Box>
													</Box>
												) : null,
											]
										)}
									/>
								)}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Box>
	)
}

export function SupplierTravelActivityBookings({
	transportServiceProvider: supplier,
}: Readonly<{
	transportServiceProvider: ISupplier
}>) {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToUrl,
		fromQuery: filtersFromUrl,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
		status: query.status || "booked",
	})
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Search
			initialParams={params}
			placeholder="Search by Guest, Services etc..."
			title="Travel Activity Bookings"
			resetParams={(params) => ({
				q: "",
				status: params.status,
			})}
			Filters={Filters}
			actions={
				<TemporaryStorage
					initUrl="/travel-activity-bookings/download"
					initParams={{
						...filtersToRequestParams(params),
						suppliers: [supplier.id],
						timezone_offset: config.timezoneOffset,
					}}
				>
					{({ message, generate, url, reset, isGenerating }) =>
						url ? (
							<Button
								as="a"
								href={url}
								target="_blank"
								download
								level="secondary"
								onClick={() => reset()}
							>
								{message}
							</Button>
						) : (
							<Button
								onClick={generate}
								disabled={isGenerating}
								level="secondary"
								size="sm"
							>
								{message || (
									<>
										<Icons.DocumentDownload />
									</>
								)}{" "}
								Excel
							</Button>
						)
					}
				</TemporaryStorage>
			}
			areAdvancedFiltersApplied={(filters) => {
				const { status, ...otherParams } = filters
				return Object.keys(otherParams).length > 0
			}}
			onSearch={(newParams) => {
				setParams({ ...newParams, page: 1 })
			}}
		>
			{({ searchParams, setSearchParams }) => (
				<Tabs layout="col">
					<TabsList>
						<TabItem
							active={searchParams.status === "booked"}
							onClick={() => {
								setSearchParams({ ...searchParams, status: "booked" })
							}}
						>
							Booked
						</TabItem>
						<TabItem
							active={searchParams.status === "assigned_but_not_booked"}
							onClick={() => {
								setSearchParams({
									...searchParams,
									status: "assigned_but_not_booked",
								})
							}}
						>
							Not Booked
						</TabItem>
						<TabItem
							active={searchParams.status === "all"}
							onClick={() => {
								setSearchParams({ ...searchParams, status: "all" })
							}}
						>
							All
						</TabItem>
					</TabsList>
					<TabContent>
						<ListView<TTravelActiviytBooking, TFilters>
							pageKey={`suppliers/${supplier.id}/travel-activity-bookings`}
							fetch={(xhr, filters) =>
								SupplierXHR(xhr).getTravelActivityBookings({
									...filtersToRequestParams(filters),
									suppliers: [supplier.id],
								})
							}
							onPageChange={(page) => setParams({ ...params, page })}
							params={params}
						>
							{({ items }) => (
								<Table
									bordered
									hover
									responsive
									headers={["Service", "Date", "Ticket", "Guest", "Amount"]}
									alignCols={{ 4: "right" }}
									rows={items.map(
										({
											currency,
											booked,
											booked_price,
											trip,
											payments_amount,
											paid_payments_amount,
											total_payments_amount,
											refunding_payments_amount,
											paid_refunding_payments_amount,
											activity,
											ticket_type,
											ticket_tourist_configurations_short_name,
											start_date,
											start_date_local,
											start_time_formatted,
											duration_formatted,
											is_booked,
											dropped_at,
										}) => [
											<Box>
												<Link
													to={generatePath(
														"/travel-activities/:travelActivityId",
														{
															travelActivityId: activity.id.toString(),
														}
													)}
													color="accent"
													anchored
												>
													{activity.name}
													{ticket_type ? ` - ${ticket_type.name}` : ``}
												</Link>
											</Box>,
											<Box>
												<Box>
													<Time
														timestamp={start_date}
														localTimestamp={start_date_local}
													/>
												</Box>
												{start_time_formatted || duration_formatted ? (
													<Box fontSize="sm" color="muted">
														{joinAttributes(
															start_time_formatted,
															duration_formatted
														)}
													</Box>
												) : null}
											</Box>,
											<Box>
												<Box>{ticket_tourist_configurations_short_name}</Box>
												<Box fontSize="sm" color="muted">
													{is_booked ? (
														<>
															<Icons.OkCircleSolid color="success" /> Booked
														</>
													) : dropped_at ? (
														<>
															<Icons.Ban color="warning" /> Dropped
														</>
													) : (
														"Pending"
													)}
												</Box>
											</Box>,
											trip ? (
												<Box>
													<Box>
														<Link
															to={generatePath("/trips/:tripId", {
																tripId: trip.id.toString(),
															})}
															color="accent"
														>
															{trip.contact.name}
														</Link>
													</Box>
													<Box fontSize="sm" color="muted">
														{joinAttributes(
															`Trip#` + trip.id,
															trip.contact.phone_numbers?.length ? (
																<PhoneNumber
																	value={trip.contact.phone_numbers}
																	iconOnly
																/>
															) : null,
															trip.contact.email ? (
																<Email value={trip.contact.email} iconOnly />
															) : null
														)}
													</Box>
												</Box>
											) : null,
											booked_price !== undefined && booked_price !== null ? (
												<Box>
													{isNumeric(booked_price) ? (
														<Inline
															fontWeight="semibold"
															justifyContent="end"
															gap="1"
														>
															{refunding_payments_amount === null ? (
																booked &&
																paid_payments_amount === payments_amount ? (
																	<Box display="inlineBlock" marginLeft="1">
																		<Icons.OkCircleSolid color="success" />
																	</Box>
																) : null
															) : paid_refunding_payments_amount ===
															  refunding_payments_amount ? (
																<Box display="inlineBlock" marginLeft="1">
																	<Icons.OkCircleSolid color="success" />
																</Box>
															) : null}
															<Text>Booking:</Text>
															{booked ? (
																<Money
																	amount={total_payments_amount || 0}
																	currency={currency}
																	showCurrency
																/>
															) : isNumeric(booked_price) ? (
																<Box>
																	<Money
																		amount={booked_price}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
															) : null}
														</Inline>
													) : null}
													{isNumeric(paid_payments_amount) ? (
														<Box
															fontWeight="semibold"
															fontSize="sm"
															title={`${formatMoneyByIntl(moneyParseByDecimal(paid_payments_amount, currency))} Paid`}
															color="success"
														>
															Paid:{" "}
															<Money
																amount={paid_payments_amount}
																currency={currency}
																showCurrency
															/>
														</Box>
													) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
													isNumeric(payments_amount) &&
													  Number(payments_amount || 0) > 0 ? (
														<Box
															fontWeight="semibold"
															fontSize="sm"
															color="muted"
														>
															Paid:{" "}
															<Money
																amount={0}
																currency={currency}
																showCurrency
															/>
														</Box>
													) : null}
													{isNumeric(refunding_payments_amount) ? (
														<Box
															fontWeight="semibold"
															fontSize="sm"
															color="warning"
															title="Refund"
															whiteSpace="nowrap"
														>
															Refund:{" "}
															<Money
																amount={paid_refunding_payments_amount || 0}
																currency={currency}
																showCurrency
															/>
															{" / "}
															<Money
																amount={refunding_payments_amount}
																currency={currency}
																showCurrency
															/>
														</Box>
													) : null}
												</Box>
											) : null,
										]
									)}
								/>
							)}
						</ListView>
					</TabContent>
				</Tabs>
			)}
		</Search>
	)
}

function Filters() {
	return (
		<Stack gap="4">
			<DatePickerField label="Start Date" name="pickup_after" />
			<DatePickerField label="End Date" name="pickup_before" />
		</Stack>
	)
}

export function SupplierPayments({
	supplierId,
	paymentType,
}: Readonly<{
	supplierId: number | string
	paymentType: "debit" | "credit"
}>) {
	const transportServiceProviders = useMemo(() => {
		if (!supplierId) return []
		return [Number(supplierId)]
	}, [supplierId])
	if (!supplierId) return null
	return (
		<Box>
			<PaymentsList
				transportServiceProviders={transportServiceProviders}
				debit={paymentType === "debit" ? true : undefined}
				hideContact
				paymentContext="suppliers"
				credit={paymentType === "credit" ? true : undefined}
			/>
		</Box>
	)
}

export function SupplierAccountings({
	supplier,
}: Readonly<{
	supplier: ISupplier
}>) {
	return (
		<Box paddingTop={"4"}>
			{supplier.accounting_accounts?.length ? (
				<Container>
					<Box textAlign="right" marginBottom="2">
						<ButtonLink
							to={generatePath("/suppliers/:supplierId/payments/outgoing", {
								supplierId: String(supplier.id),
							})}
						>
							View Due Payments <Icons.ChevronDown rotate="270" />
						</ButtonLink>
					</Box>
					<AccountsList accounts={supplier.accounting_accounts} />
				</Container>
			) : (
				<Container>
					<Stack gap="4">
						<Text fontSize="md" fontWeight="semibold">
							No accounting activities for this service provider
						</Text>
						<Text color="muted">
							Log payments regarding bookings or services of this service
							provider to manage the accounting.
						</Text>
						<Box>
							<ButtonLink
								to={generatePath("/suppliers/:supplierId/payments/outgoing", {
									supplierId: String(supplier.id),
								})}
								status="primary"
							>
								View Due Payments <Icons.ChevronDown rotate="270" />
							</ButtonLink>
						</Box>
					</Stack>
				</Container>
			)}
		</Box>
	)
}

export function SelectSupplierContact({
	supplierId,
	onCreateSuccess,
	...props
}: Omit<React.ComponentProps<typeof SelectContact>, "onCreate" | "options"> & {
	supplierId: number | string
	onCreateSuccess?: (contact: IContact) => void
}) {
	const { supplier, revalidate } = useSupplier({ supplierId })
	const xhr = useXHR()
	return (
		<SelectContact
			{...props}
			phoneNumberRequired
			options={supplier?.contacts?.filter((c) => !c.deleted_at) || []}
			onCreate={async (data) => {
				const contact = await SupplierXHR(xhr).createContact(supplierId, data)
				revalidate()
				onCreateSuccess?.(contact)
				return contact
			}}
		/>
	)
}

function TripWiseBookingsFilters() {
	return (
		<Stack gap="4">
			<DateRangePickerField
				fromName="trips_start_date_after"
				toName="trips_start_date_before"
				label="Trip Between"
			/>
			<SelectInputField label="Status" name="status">
				<option value="">Select Status</option>
				<option value="booked">Fully Booked</option>
				<option value="partially_booked">Partially Booked</option>
				<option value="not_booked">Not Booked</option>
			</SelectInputField>
		</Stack>
	)
}
export function SupplierTripWiseBookings({
	transportServiceProvider: supplier,
}: Readonly<{
	transportServiceProvider: ISupplier
}>) {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToUrl,
		fromQuery: filtersFromUrl,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
	})
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])
	return (
		<Box>
			<Search
				initialParams={params}
				placeholder="Search by Guest, Trip Id etc..."
				title="Trip Wise Cab Schedules"
				Filters={TripWiseBookingsFilters}
				actions={
					<Inline gap={"2"}>
						<TemporaryStorage
							initUrl={`/trip-wise-cab-schedule/download`}
							initParams={{
								...filtersToRequestParams(params),
								supplier: supplier.id,
							}}
						>
							{({ generate, url, message, reset, isGenerating }) =>
								url ? (
									<Button
										as="a"
										href={url}
										target="_blank"
										download
										level="secondary"
										onClick={() => reset()}
									>
										{message}
									</Button>
								) : (
									<Button
										onClick={generate}
										disabled={isGenerating}
										level="secondary"
									>
										{message || (
											<>
												<Icons.DocumentDownload /> Excel
											</>
										)}
									</Button>
								)
							}
						</TemporaryStorage>
						<ButtonLink
							to={generatePath(
								`/suppliers/:supplierId/bookings/cab-schedules`,
								{ supplierId: supplier.id.toString() }
							)}
						>
							Cab Schedules
						</ButtonLink>
					</Inline>
				}
				areAdvancedFiltersApplied={(filters) => {
					const { ...otherParams } = filters
					return Object.keys(otherParams).length > 0
				}}
				onSearch={(newParams) => {
					setParams({ ...newParams, page: 1 })
				}}
			>
				<ListView<ITSPTripWiseCabsSchedule, TFilters>
					pageKey={`suppliers/${supplier.id}/trip-wise-booking`}
					fetch={(xhr, filters) =>
						SupplierXHR(xhr).getTripWiseBookings({
							...filtersToRequestParams(filters),
							supplier: supplier.id,
						})
					}
					onPageChange={(page) => setParams({ ...params, page })}
					params={params}
				>
					{({ items }) => (
						<Table
							bordered
							hover
							responsive
							headers={[
								"Trip Info",
								"Service Dates",
								"First Service",
								"Last Service",
								"Cabs Type/Cabs",
								"Driver",
								"Amount",
								"",
							]}
							alignCols={{ 6: "right", 7: "right" }}
							rows={items.map((item) => {
								const { contact, id, schedules } = item

								const bookingPrices: TMoney[] = []
								const givenPrices: TMoney[] = []
								const refundAmounts: TMoney[] = []
								const paidRefundAmount: TMoney[] = []
								let isTotalPaid = true

								const driversOnTrip = new Map<string, TDriver[]>()
								const cabsOnTrip = new Map()

								let allBooked = true
								let allUnbooked = true
								let cabTypesCount = 0
								let driverCount = 0

								const grouped: Record<string, ICabSchedule[]> = {}

								for (const sch of schedules) {
									const cabsPerService = new Map()
									const driversPerService = new Map<string, TDriver>()

									const key = sch.start_date
									if (!grouped[key]) grouped[key] = []
									grouped[key].push(sch)

									for (const cab of sch.cabs) {
										if (cab.booked) {
											allUnbooked = false
										} else {
											allBooked = false
										}

										// Process cabs per service
										if (cabTypesCount < 3) {
											const cabKey = cab.cab ? cab.cab.name : cab.cab_type.name
											const existing = cabsPerService.get(cabKey) ?? {
												...(cab.cab || cab.cab_type),
												count: 0,
											}
											existing.count += 1
											cabsPerService.set(cabKey, existing)
										}

										// Process drivers per service
										if (cab.driver?.name && driverCount < 3) {
											driversPerService.set(cab.driver.name, cab.driver)
										}

										// Process payment amounts
										if (cab.booked_price_money)
											bookingPrices.push(cab.booked_price_money)
										if (cab.paid_payments_amount_money)
											givenPrices.push(cab.paid_payments_amount_money)
										if (cab.refunding_payments_amount_money)
											refundAmounts.push(cab.refunding_payments_amount_money)
										if (cab.paid_refunding_payments_amount_money)
											paidRefundAmount.push(
												cab.paid_refunding_payments_amount_money
											)
										if (
											numberToLocalString(cab.booked_price) >
											numberToLocalString(cab.paid_payments_amount)
										) {
											isTotalPaid = false
										}
									}

									// Set for cabs per trip
									if (cabTypesCount < 3) {
										const cabKeyStr = Array.from(cabsPerService.values())
											.map((cab) => `${cab.count} ${cab.name}`)
											.join(" + ")
										if (!cabsOnTrip.has(cabKeyStr)) {
											cabTypesCount += cabsPerService.size
										}
										cabsOnTrip.set(cabKeyStr, [...cabsPerService.values()])
									}

									// Set for drivers per trip
									if (driverCount < 3 && driversPerService.size > 0) {
										const driversKey = Array.from(driversPerService.values())
											.map((driver) => driver.name)
											.join(" + ")
										if (!driversOnTrip.has(driversKey)) {
											driverCount += driversPerService.size
										}
										driversOnTrip.set(driversKey, [
											...driversPerService.values(),
										])
									}
								}

								// Determine booking status
								const bookingStatus:
									| "booked"
									| "not_booked"
									| "partially_booked" = allBooked
									? "booked"
									: allUnbooked
										? "not_booked"
										: "partially_booked"

								// Sort grouped schedules by date
								const sortedEntries = Object.entries(grouped).sort(
									([a], [b]) => {
										return new Date(a).getTime() - new Date(b).getTime()
									}
								)

								// Convert to ordered Map
								const dateWiseSchedules = new Map<string, ICabSchedule[]>(
									sortedEntries
								)
								const sortedDateWiseSchedules = Array.from(
									dateWiseSchedules.entries()
								)

								return [
									<Box>
										<Box>
											<Link
												to={generatePath("/trips/:tripId", {
													tripId: id.toString(),
												})}
												color="accent"
											>
												{id}
											</Link>
										</Box>
										<Text fontSize="sm" color="muted">
											{contact.name}
										</Text>
									</Box>,
									<Box>
										<Inline alignItems={"center"} gap="1" flexWrap="wrap">
											<Box>
												<Time
													timestamp={
														sortedDateWiseSchedules[0][1][0].start_date
													}
													localTimestamp={
														sortedDateWiseSchedules[0][1][0].start_date_local
													}
													whiteSpace="nowrap"
													format="DD MMM"
												/>
												{sortedDateWiseSchedules.length > 1 ? (
													<>
														-
														<Time
															timestamp={
																sortedDateWiseSchedules[
																	sortedDateWiseSchedules.length - 1
																][1][0].start_date
															}
															localTimestamp={
																sortedDateWiseSchedules[
																	sortedDateWiseSchedules.length - 1
																][1][0].start_date_local
															}
															whiteSpace="nowrap"
															format="DD MMM"
														/>
													</>
												) : null}
											</Box>

											<Box>
												({sortedDateWiseSchedules.length} Day
												{sortedDateWiseSchedules.length > 1 ? "s" : ""})
											</Box>
										</Inline>
									</Box>,
									<Box>
										<Box>
											<Box fontWeight="semibold">
												{
													sortedDateWiseSchedules[0][1][0].transport_service
														.short_name
												}
											</Box>
											<Box fontSize="sm" color="muted">
												•{" "}
												{
													sortedDateWiseSchedules[0][1][0].transport_service
														.service
												}
											</Box>
										</Box>
									</Box>,
									<Box>
										{sortedDateWiseSchedules.length > 1 ? (
											<Box>
												<Box fontWeight="semibold">
													{
														sortedDateWiseSchedules[
															sortedDateWiseSchedules.length - 1
														][1][0].transport_service.short_name
													}
												</Box>
												<Box fontSize="sm" color="muted">
													•{" "}
													{
														sortedDateWiseSchedules[
															sortedDateWiseSchedules.length - 1
														][1][0].transport_service.service
													}
												</Box>
											</Box>
										) : (
											<Box>-</Box>
										)}
									</Box>,
									<Box>
										<Inline gap={"1"} alignItems={"start"}>
											<Box fontSize="sm">
												{bookingStatus === "booked" ? (
													<Icons.OkCircleSolid color="success" />
												) : bookingStatus === "partially_booked" ? (
													<Icons.OkCircle />
												) : (
													<Icons.XCircle color={"danger"} />
												)}{" "}
											</Box>

											<Stack gap="1" alignItems={"start"}>
												{cabTypesCount < 3 ? (
													Array.from(cabsOnTrip.entries()).map(
														([key, values], idx) => (
															<Inline
																gap={"1"}
																key={idx}
																flexWrap={"wrap"}
																alignItems={"end"}
															>
																{values.map(
																	(
																		cabs: ICab & { count: number },
																		id: number
																	) => (
																		<Inline
																			key={id}
																			gap={"1"}
																			alignItems={"end"}
																		>
																			<Inline
																				key={id}
																				gap={"1"}
																				flexWrap={"wrap"}
																				alignItems={"start"}
																			>
																				<Text whiteSpace="nowrap">
																					{cabs.count} {cabs.name}
																				</Text>{" "}
																				{cabs.number_plate ? (
																					<Badge>{cabs.number_plate}</Badge>
																				) : null}
																				{id !== values.length - 1 ? (
																					<Text fontWeight="semibold"> + </Text>
																				) : null}
																			</Inline>
																		</Inline>
																	)
																)}

																{idx !== cabsOnTrip.size - 1 ? (
																	<Text fontWeight="semibold"> OR </Text>
																) : null}
															</Inline>
														)
													)
												) : (
													<DetailedTripSchedule
														tripWiseBookings={item}
														dateWiseSchedules={sortedDateWiseSchedules}
													>
														{({ onShow }) => (
															<Box
																onClick={onShow}
																cursor="pointer"
																color={"accent"}
															>
																{" "}
																Multiple Cabs
															</Box>
														)}
													</DetailedTripSchedule>
												)}
											</Stack>
										</Inline>
									</Box>,
									<Box>
										{driverCount === 0 ? (
											<Text color="muted">N/A</Text>
										) : driverCount < 3 ? (
											<Inline gap={"1"}>
												<Box>
													{Array.from(driversOnTrip.entries()).map(
														([key, values], idx) => {
															return (
																<Inline
																	key={idx}
																	gap={"1"}
																	alignItems={"end"}
																	flexWrap={"wrap"}
																>
																	{values.map((driver, id) => (
																		<Inline
																			key={id}
																			gap={"1"}
																			alignItems={"end"}
																		>
																			<Text>{driver.name}</Text>
																			{id !== values.length - 1 ? (
																				<Text fontWeight="semibold">+</Text>
																			) : null}
																		</Inline>
																	))}
																	{idx !== driversOnTrip.size - 1 ? (
																		<Box fontWeight="semibold"> OR </Box>
																	) : null}
																</Inline>
															)
														}
													)}
												</Box>
											</Inline>
										) : (
											<Text whiteSpace="nowrap" fontWeight="semibold">
												Multiple Drivers
											</Text>
										)}
									</Box>,
									<Box>
										<BookingAmountSummary
											booking_prices={bookingPrices}
											given_price={givenPrices}
											refund_amount={refundAmounts}
											paid_refund_amount={paidRefundAmount}
											isTotalPaid={isTotalPaid}
										/>
									</Box>,
									<Box>
										<DetailedTripSchedule
											tripWiseBookings={item}
											dateWiseSchedules={sortedDateWiseSchedules}
										>
											{({ onShow }) => (
												<Button onClick={onShow}>
													<Icons.ArrowExpand />
												</Button>
											)}
										</DetailedTripSchedule>
									</Box>,
								]
							})}
						/>
					)}
				</ListView>
			</Search>
		</Box>
	)
}

export function BookingAmountSummary({
	booking_prices,
	given_price,
	refund_amount,
	paid_refund_amount,
	isTotalPaid,
}: Readonly<{
	booking_prices: TMoney[]
	given_price: TMoney[]
	refund_amount: TMoney[]
	paid_refund_amount: TMoney[]
	isTotalPaid: boolean
}>) {
	const totalBooking = addMoneyFromDifferentCurrencies(booking_prices)
	const totalPaid = addMoneyFromDifferentCurrencies(given_price)
	const totalRefund = addMoneyFromDifferentCurrencies(refund_amount)
	const totalPaidRefund = addMoneyFromDifferentCurrencies(paid_refund_amount)
	function moneyArraysEqual(a: TMoney[], b: TMoney[]) {
		if (a.length !== b.length) return false
		const normalize = (arr: TMoney[]) =>
			arr
				.map((m) => `${m.currency}:${m.amount}`)
				.sort()
				.join("|")
		return normalize(a) === normalize(b)
	}

	return (
		<Stack alignItems="end">
			{totalBooking.length > 0 ? (
				<Inline
					fontWeight="semibold"
					gap="1"
					alignItems="center"
					justifyContent="end"
				>
					{totalRefund.length === 0 ? (
						isTotalPaid ? (
							<Box display="inlineBlock" marginLeft="1">
								<Icons.OkCircleSolid color="success" />
							</Box>
						) : null
					) : moneyArraysEqual(totalRefund, totalPaidRefund) ? (
						<Box display="inlineBlock" marginLeft="1">
							<Icons.OkCircleSolid color="success" />
						</Box>
					) : null}
					<Text>Booking:</Text>
					<MoneySum money={totalBooking} />
				</Inline>
			) : null}

			{totalPaid.length > 0 ? (
				<Box fontWeight="semibold" fontSize="sm" color="success">
					Paid: <MoneySum money={totalPaid} />
				</Box>
			) : totalBooking.length > 0 ? (
				<Box fontWeight="semibold" fontSize="sm" color="muted">
					Paid: <MoneySum money={[]} />
				</Box>
			) : null}

			{totalRefund.length > 0 ? (
				<Box
					fontWeight="semibold"
					fontSize="sm"
					color="warning"
					title="Refund"
					whiteSpace="nowrap"
				>
					Refund: <MoneySum money={totalPaidRefund || 0} />
					{" / "}
					<MoneySum money={totalRefund || 0} />
				</Box>
			) : null}
		</Stack>
	)
}

function DetailedTripSchedule({
	tripWiseBookings,
	dateWiseSchedules,
	children,
}: Readonly<{
	tripWiseBookings: ITSPTripWiseCabsSchedule
	dateWiseSchedules: Array<[string, ICabSchedule[]]>
	children: (props: { onShow: () => void }) => React.ReactNode
}>) {
	const { days, start_date, pax_string, nights, destinations, contact, id } =
		tripWiseBookings
	const [isOpen, open, close] = useDialog()

	return (
		<>
			{children({ onShow: () => open() })}
			{isOpen ? (
				<Dialog open onClose={close} title="All Cab Schedules" lg>
					<Box padding="4" bgColor={"inset"}>
						<Box paddingX={"2"}>
							<Heading as="h2" fontSize="lg">
								<Link
									to={generatePath("/trips/:tripId", {
										tripId: id.toString(),
									})}
									color="accent"
								>
									<Text as="span" title="Trip ID">
										<Text as="span" opacity="50">
											#
										</Text>
										&nbsp;<Text as="span">{id}</Text>
									</Text>{" "}
									• {contact.name} •{" "}
									{destinations.map((t) => t.name).join(", ")}
								</Link>
							</Heading>

							<Box whiteSpace="preserve">
								<Inline
									alignItems={{ md: "center" }}
									collapseBelow="sm"
									gap="2"
								>
									<Inline gap="2" alignItems="center">
										<Icons.Calendar color="muted" />
										<Box>
											{joinAttributes(
												<Time value={start_date} />,
												<TimeDuration days={days}>
													{nights}N, {days}D
												</TimeDuration>
											)}
										</Box>
									</Inline>
									<Inline gap="2" alignItems="center">
										<Icons.UserGroup color="muted" />
										<Box>{pax_string}</Box>
									</Inline>
								</Inline>
							</Box>

							<Stack gap={"4"} paddingTop={"4"}>
								{dateWiseSchedules.map(
									([date, schedules]: [string, ICabSchedule[]], index) => (
										<Inline
											position="relative"
											gap="4"
											collapseBelow="sm"
											key={index}
										>
											<Box position="sticky" top="0" zIndex="10">
												<Stack
													bgColor="default"
													rounded="lg"
													borderWidth="1"
													padding="4"
													fontWeight="semibold"
													alignItems={"center"}
													style={{ width: "8rem" }}
												>
													<Time
														timestamp={date}
														localTimestamp={schedules[0].start_date_local}
														dateFormat={"DD MMMM"}
														whiteSpace="nowrap"
													/>
													<Time
														timestamp={date}
														localTimestamp={schedules[0].start_date_local}
														dateFormat={"ddd"}
														color={"muted"}
														fontWeight="normal"
														whiteSpace="nowrap"
													/>
												</Stack>
											</Box>

											<Box flex={"1"}>
												<Stack
													gap={"4"}
													bgColor="default"
													rounded="lg"
													borderWidth="1"
													paddingY="2"
												>
													{schedules.map(
														({ transport_service, cabs, id }, index) => (
															<Box
																key={index}
																borderTopWidth={index > 0 ? "1" : undefined}
																paddingTop={index > 0 ? "2" : undefined}
																paddingX={"3"}
															>
																<Inline
																	gap="2"
																	alignItems="start"
																	collapseBelow="md"
																	justifyContent={"between"}
																>
																	<Box paddingBottom={"2"} width={"1/3"}>
																		<Box fontWeight="semibold">
																			{transport_service.short_name}
																		</Box>
																		<Box fontSize="sm" color="muted">
																			• {transport_service.service}
																		</Box>
																	</Box>
																	<Stack width={"full"}>
																		{cabs.map(
																			(
																				{
																					cab,
																					cab_type,
																					latest_booking_stage,
																					booked_price,
																					total_payments_amount,
																					currency,
																					paid_payments_amount,
																					refunding_payments_amount,
																					paid_refunding_payments_amount,
																					driver,
																				},
																				index
																			) => (
																				<Grid
																					gap="2"
																					key={index}
																					borderTopWidth={
																						index > 0 ? "1" : undefined
																					}
																					paddingY="2"
																					paddingRight="2"
																				>
																					<Col>
																						<Inline
																							gap={"1"}
																							alignItems="center"
																							flexWrap={"wrap"}
																						>
																							{latest_booking_stage.name ===
																							"booked" ? (
																								<Icons.OkCircleSolid color="success" />
																							) : (
																								<Icons.XCircle
																									color={"danger"}
																								/>
																							)}
																							{cab ? (
																								<Box>
																									<Box>{cab.name}</Box>
																									<Box
																										fontSize="sm"
																										color="muted"
																									>
																										{cab.number_plate}
																									</Box>
																								</Box>
																							) : (
																								<Box>{cab_type.name}</Box>
																							)}
																						</Inline>
																					</Col>
																					<Col>
																						{" "}
																						{driver ? (
																							<Box>
																								<Stack
																									gap="2"
																									alignItems="start"
																								>
																									<Inline gap="1">
																										<Box>
																											<Icons.SteeringWheel />
																										</Box>
																										<Box>{driver.name}</Box>
																									</Inline>{" "}
																									<Box
																										fontSize="sm"
																										color="muted"
																									>
																										{joinAttributes(
																											driver.phone_number ? (
																												<PhoneNumber
																													value={
																														driver.phone_number
																													}
																													color={"accent"}
																													title="Contact"
																												/>
																											) : null,
																											driver.email ? (
																												<Email
																													value={driver.email}
																													iconOnly
																												/>
																											) : null
																										)}
																									</Box>
																								</Stack>
																							</Box>
																						) : null}
																					</Col>
																					<Col
																						textAlign={"right"}
																						xl={2}
																						lg={2}
																						md={2}
																						sm={2}
																						xs={2}
																					>
																						{booked_price !== undefined &&
																						booked_price !== null ? (
																							<Box>
																								<Box>
																									<Box fontWeight="semibold">
																										{latest_booking_stage.is_same_or_after_booked ? (
																											<Money
																												showCurrency
																												amount={
																													total_payments_amount ||
																													0
																												}
																												currency={currency}
																											/>
																										) : isNumeric(
																												booked_price
																										  ) ? (
																											<Box>
																												<Money
																													showCurrency
																													amount={booked_price}
																													currency={currency}
																												/>
																											</Box>
																										) : null}
																									</Box>
																									{isNumeric(
																										paid_payments_amount
																									) ? (
																										<Box
																											fontSize="sm"
																											title={`${formatMoneyByIntl(
																												moneyParseByDecimal(
																													paid_payments_amount,
																													currency
																												)
																											)} Paid`}
																											marginTop="1"
																											color="success"
																										>
																											<Icons.Ok />
																											<Box
																												display="inlineBlock"
																												marginLeft="px"
																											>
																												<Money
																													amount={
																														paid_payments_amount
																													}
																													currency={currency}
																													showCurrency
																												/>
																											</Box>
																										</Box>
																									) : null}
																									{isNumeric(
																										refunding_payments_amount
																									) ? (
																										<Box
																											fontSize="sm"
																											title={`Refund: ${formatMoneyByIntl(moneyParseByDecimal(paid_refunding_payments_amount || 0, currency))}/${formatMoneyByIntl(moneyParseByDecimal(refunding_payments_amount, currency))}`}
																											marginTop="1"
																											color="warning"
																										>
																											<Icons.Refresh color="warning" />
																											<Box
																												display="inlineBlock"
																												marginLeft="px"
																											>
																												<Money
																													amount={
																														refunding_payments_amount
																													}
																													currency={currency}
																													showCurrency
																												/>
																											</Box>
																										</Box>
																									) : null}
																								</Box>
																							</Box>
																						) : null}
																					</Col>
																				</Grid>
																			)
																		)}
																	</Stack>
																</Inline>
															</Box>
														)
													)}
												</Stack>
											</Box>
										</Inline>
									)
								)}
							</Stack>
						</Box>
					</Box>
				</Dialog>
			) : null}
		</>
	)
}

function HotelFilters() {
	const { value: reportType } =
		useFieldValue<TFilters["report_type"]>("report_type")
	const { value: reportStage } =
		useFieldValue<TFilters["report_stage"]>("report_stage")
	const form = useForm()
	const isCheckInReport = reportType === "overall_checkin" || false
	return (
		<Stack gap="4">
			{reportStage === "all" ? (
				<Stack gap="1">
					<label
						htmlFor={
							isCheckInReport
								? "overall_checkin_after"
								: "overall_checkout_after"
						}
					>
						{isCheckInReport ? "Check-In Date Range" : "Check-Out Date Range"}
					</label>
					<Box display="flex" alignItems="center" marginTop="1">
						<DatePickerField
							name={
								isCheckInReport
									? "overall_checkin_after"
									: "overall_checkout_after"
							}
							onChange={(value) => {
								form.change(
									isCheckInReport
										? "overall_checkin_after"
										: "overall_checkout_after",
									startOf(value || new Date(), "day")
								)
							}}
						/>
						<Box>
							<Icons.ArrowLeft rotate="180" />
						</Box>
						<DatePickerField
							name={
								isCheckInReport
									? "overall_checkin_before"
									: "overall_checkout_before"
							}
							onChange={(value) => {
								form.change(
									isCheckInReport
										? "overall_checkin_before"
										: "overall_checkout_before",
									endOf(value || new Date(), "day")
								)
							}}
							rightAlign
						/>
					</Box>
				</Stack>
			) : null}

			<SelectField
				label="Payment Status"
				select={SelectHotelBookingPaymentStatuses}
				name="payment_statuses"
				multiple
			/>
		</Stack>
	)
}
export function HotelSupplierBooking({
	supplier,
}: Readonly<{ supplier: ISupplier }>) {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToUrl,
		fromQuery: filtersFromUrl,
	})
	const [params, setParams] = useSearch<TFilters>({
		...query,
		report_stage: query.report_stage || "upcoming",
		report_type: query.report_type || "overall_checkin",
		overall_checkin_after:
			query.overall_checkin_after || startOf(new Date(), "day").toISOString(),
	})
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	return (
		<Box>
			<Search
				initialParams={params}
				placeholder="Search by Guest, Services etc..."
				title="Hotel Bookings"
				resetParams={(params) => ({
					q: "",
					report_stage: params.report_stage,
					report_type: params.report_type,
				})}
				Filters={HotelFilters}
				areAdvancedFiltersApplied={(filters) => {
					const {
						report_stage,
						overall_checkin_after,
						overall_checkin_before,
						overall_checkout_after,
						overall_checkout_before,
						report_type,
						...otherParams
					} = filters
					return areAdvancedFiltersAppliedDefault(otherParams)
				}}
				onSearch={(newParams) => {
					setParams(newParams)
				}}
				actions={({ setSearchParams, searchParams }) => {
					return (
						<Inline gap="2" flexWrap="wrap">
							<Button
								status="primary"
								onClick={() => {
									const isCheckInReport =
										searchParams.report_type === "overall_checkin" || false
									setSearchParams({
										...searchParams,
										page: 1,
										report_type: isCheckInReport
											? "overall_checkout"
											: "overall_checkin",

										overall_checkin_before: isCheckInReport
											? undefined
											: searchParams.overall_checkout_before,
										overall_checkin_after: isCheckInReport
											? undefined
											: searchParams.overall_checkout_after,
										overall_checkout_before: isCheckInReport
											? searchParams.overall_checkin_before
											: undefined,
										overall_checkout_after: isCheckInReport
											? searchParams.overall_checkin_after
											: undefined,
									})
								}}
							>
								{searchParams.report_type === "overall_checkin"
									? "Check-Outs"
									: "Check-Ins"}
							</Button>
							<TemporaryStorage
								initUrl="/hotel-bookings/export"
								initParams={{
									supplier: supplier.id,
									...filtersToRequestParams(params),
									timezone_offset: config.timezoneOffset,
								}}
							>
								{({ generate, url, message, reset, isGenerating }) =>
									url ? (
										<Button
											as="a"
											href={url}
											target="_blank"
											download
											level="secondary"
											onClick={() => reset()}
										>
											{message}
										</Button>
									) : (
										<Button
											onClick={generate}
											disabled={isGenerating}
											level="secondary"
										>
											{message || (
												<>
													<Icons.DocumentDownload /> Excel
												</>
											)}
										</Button>
									)
								}
							</TemporaryStorage>
						</Inline>
					)
				}}
			>
				{({ searchParams, setSearchParams }) => (
					<Tabs layout="col" gap={"0"}>
						<TabsList>
							<TabItem
								active={searchParams.report_stage === "upcoming"}
								onClick={() => {
									const currentDate = startOf(new Date(), "day").toISOString()
									const reportType = searchParams.report_type
									setSearchParams({
										...searchParams,
										report_stage: "upcoming",
										report_type: reportType,
										overall_checkin_after:
											reportType === "overall_checkin"
												? currentDate
												: undefined,
										overall_checkout_after:
											reportType === "overall_checkout"
												? currentDate
												: undefined,
										overall_checkin_before: undefined,
										overall_checkout_before: undefined,
									})
								}}
							>
								Upcoming
							</TabItem>
							<TabItem
								active={searchParams.report_stage === "past"}
								onClick={() => {
									const currentDate = startOf(
										subtractUnit(new Date(), 1, "day"),
										"day"
									).toISOString()
									const reportType = searchParams.report_type
									setSearchParams({
										...searchParams,
										report_stage: "past",
										report_type: searchParams.report_type,
										overall_checkin_before:
											reportType === "overall_checkin"
												? currentDate
												: undefined,
										overall_checkout_before:
											reportType === "overall_checkout"
												? currentDate
												: undefined,
										overall_checkin_after: undefined,
										overall_checkout_after: undefined,
									})
								}}
							>
								Past
							</TabItem>
							<TabItem
								active={searchParams.report_stage === "all"}
								onClick={() => {
									setSearchParams({
										...searchParams,
										report_stage: "all",
										report_type: searchParams.report_type,
										overall_checkin_before: undefined,
										overall_checkin_after: undefined,
										overall_checkout_before: undefined,
										overall_checkout_after: undefined,
									})
								}}
							>
								All
							</TabItem>
						</TabsList>
						<TabContent>
							<ListView<IBooking & { trip: ITrip }, TFilters>
								pageKey={`supplier/${supplier.id}/hotel-bookings`}
								fetch={(xhr, filters) =>
									SupplierXHR(xhr).getHotelBookings({
										...filtersToRequestParams(filters),
										supplier: [supplier.id],
									})
								}
								onPageChange={(page) => setParams({ ...params, page })}
								params={params}
							>
								{({ items }) => (
									<Table
										hover
										responsive
										headers={[
											"TripId",
											"Guest",
											"Check-in/Checkout",
											"Room/Services",
											"Meal Plan",
											"Status",
											"Price",
										]}
										bordered
										alignCols={{
											6: "right",
										}}
										rows={items.map((booking, idx) => {
											const {
												can_view_prices,
												booked_price,
												payments_amount,
												refunding_payments_amount,
												cancellation_charges,
												paid_payments_amount,
												paid_refunding_payments_amount,
												currency,
												room_nights,
												no_of_nights,
												trip,
												checkin,
												overall_checkin,
												overall_checkin_local,
												overall_checkout,
												overall_checkout_local,
												details,
												booking_stage,
											} = booking

											const trip_start_date = localOrUtcTimestampToLocalDate(
												trip.start_date_local,
												trip.start_date
											)

											const stay_nights = [
												getDiff(checkin, trip_start_date, "days") + 1,
											]
											for (let i = 1; i < no_of_nights; i++) {
												stay_nights.push(stay_nights[i - 1] + 1)
											}

											return [
												<Box>
													<Link
														to={`/trips/${trip.id}/services-bookings/hotels`}
														anchored
														color="accent"
													>
														#{trip.id}
													</Link>
												</Box>,

												<Box>
													{trip.tourist?.name || ""}{" "}
													<Text as="span">
														{joinAttributes(
															[trip.no_of_adults, `${trip.no_of_adults}A`],
															[trip.no_of_children, `${trip.no_of_children}C`]
														)}
													</Text>
												</Box>,
												<Box>
													<Inline gap="1">
														<Time
															timestamp={overall_checkin}
															localTimestamp={overall_checkin_local}
															format="D MMM"
															whiteSpace="nowrap"
														/>
														-
														<Time
															timestamp={overall_checkout}
															localTimestamp={overall_checkout_local}
															whiteSpace="nowrap"
															format="D MMM"
														/>
														<Box>({(stay_nights || []).length}N)</Box>
													</Inline>
												</Box>,
												<Box>
													{(room_nights?.length || 0) > 0 ? (
														(room_nights || []).map(function (
															{ room_type, no_of_rooms },
															index,
															array
														) {
															return (
																<Box key={room_type.id}>
																	{no_of_rooms} {room_type.name}
																	{index < array.length - 1 ? ", " : ""}
																</Box>
															)
														})
													) : (
														<Box fontSize="xs" color="muted" marginTop="1">
															No Room Services
														</Box>
													)}
												</Box>,
												<Box>
													{details?.length ? (
														<Box>
															{(() => {
																const grouped: Array<
																	{
																		night: number
																		meal_plan: IMealPlan
																	}[]
																> = []
																let group: {
																	night: number
																	meal_plan: IMealPlan
																}[] = []

																details.forEach((detail, i) => {
																	const { meal_plan } = detail
																	const night = i + 1

																	const currentDetail = { night, meal_plan }

																	if (
																		group.length === 0 ||
																		group[group.length - 1].meal_plan.name ===
																			meal_plan.name
																	) {
																		group.push(currentDetail)
																	} else {
																		grouped.push(group)
																		group = [currentDetail]
																	}
																})

																if (group.length > 0) grouped.push(group)

																const allSameMealPlan =
																	grouped.length === 1 &&
																	grouped[0].length === details.length

																if (allSameMealPlan) {
																	return (
																		<Box>{grouped[0][0].meal_plan.name}</Box>
																	)
																}
																return grouped.map((group, idx) => {
																	const mealPlanName = group[0].meal_plan.name
																	const nightList = group
																		.map((g) => `${g.night}N`)
																		.join(", ")
																	return (
																		<Box key={idx}>
																			{nightList} - {mealPlanName}
																		</Box>
																	)
																})
															})()}
														</Box>
													) : (
														<Box fontSize="xs" color="muted" marginTop="1">
															No meal plan
														</Box>
													)}
												</Box>,
												<Box>
													{booking_stage.state === "booked" ? (
														<Box display="inlineBlock" marginRight="1">
															<Icons.OkCircleSolid color="success" />
														</Box>
													) : null}
													<Box as="span" fontWeight="semibold">
														{getDisplayNameForStage(booking_stage.state)}
													</Box>
												</Box>,

												can_view_prices ? (
													<Box
														verticalAlign="top"
														style={{ textAlign: "right" }}
													>
														{isNumeric(booked_price) ? (
															<Inline
																fontWeight="semibold"
																alignItems="center"
																justifyContent="end"
																gap="1"
															>
																{refunding_payments_amount === null ? (
																	[
																		"changed",
																		"booked",
																		"dropped",
																		"trip_dropped",
																	].includes(booking.booking_stage.state) &&
																	paid_payments_amount === payments_amount ? (
																		<Box display="inlineBlock" marginLeft="1">
																			<Icons.OkCircleSolid color="success" />
																		</Box>
																	) : null
																) : paid_refunding_payments_amount ===
																  refunding_payments_amount ? (
																	<Box display="inlineBlock" marginLeft="1">
																		<Icons.OkCircleSolid color="success" />
																	</Box>
																) : null}
																<Box>Booking:</Box>
																<Money
																	money={moneyParseByDecimal(
																		booked_price,
																		currency
																	)}
																	showCurrency
																/>
															</Inline>
														) : null}

														{isNumeric(payments_amount) ? (
															<Box
																fontWeight="semibold"
																fontSize="sm"
																color="muted"
															>
																{isNumeric(refunding_payments_amount) &&
																isNumeric(cancellation_charges) ? (
																	<Box title="Net Payable">
																		Net Payable:{" "}
																		<Money
																			amount={cancellation_charges}
																			currency={currency}
																			showCurrency
																		/>
																	</Box>
																) : isNumeric(booked_price) &&
																  Number(payments_amount || 0) ===
																		Number(booked_price || 0) ? null : (
																	<Box title="Net Payable">
																		Net Payable:{" "}
																		<Money
																			amount={payments_amount}
																			currency={currency}
																			showCurrency
																		/>
																	</Box>
																)}
															</Box>
														) : //show net payaable as 0 if booking_status is either changed or trip dropped
														booking.booking_stage.state === "changed" ||
														  booking.booking_stage.state === "dropped" ||
														  booking.booking_stage.state === "trip_dropped" ? (
															<Box
																fontWeight="semibold"
																fontSize="sm"
																color="muted"
																whiteSpace="nowrap"
																title="Net Payable"
															>
																Net Payable:{" "}
																<Money
																	amount={0}
																	currency={currency}
																	showCurrency
																/>
															</Box>
														) : null}
														{isNumeric(paid_payments_amount) ? (
															<>
																<Box
																	fontWeight="semibold"
																	fontSize="sm"
																	color="success"
																>
																	Amount Paid:{" "}
																	<Money
																		amount={paid_payments_amount}
																		currency={currency}
																		showCurrency
																	/>
																</Box>
																{isNumeric(payments_amount) &&
																	payments_amount > paid_payments_amount && (
																		<Box
																			fontWeight="semibold"
																			fontSize="sm"
																			color="muted"
																		>
																			Due Payable:{" "}
																			<Money
																				amount={
																					payments_amount - paid_payments_amount
																				}
																				currency={currency}
																				showCurrency
																			/>
																		</Box>
																	)}
															</>
														) : //show paid=0 when "paid_payments_amount" is null && "payments_amount" is not null & "payments_amount" is greater than 0
														isNumeric(payments_amount) &&
														  Number(payments_amount || 0) > 0 ? (
															<Box
																fontWeight="semibold"
																fontSize="sm"
																color="muted"
																title="Paid"
															>
																Amount Paid:{" "}
																<Money
																	amount={0}
																	currency={currency}
																	showCurrency
																/>
															</Box>
														) : null}
														{isNumeric(refunding_payments_amount) ? (
															<Box
																fontWeight="semibold"
																fontSize="sm"
																color="warning"
																title="Refund"
															>
																Refund:{" "}
																<Money
																	amount={paid_refunding_payments_amount || 0}
																	currency={currency}
																	showCurrency
																/>
																{" / "}
																<Money
																	amount={refunding_payments_amount}
																	currency={currency}
																	showCurrency
																/>
															</Box>
														) : null}
													</Box>
												) : null,
											]
										})}
									/>
								)}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Box>
	)
}

export function VisaSupplierBooking({
	supplier,
}: Readonly<{ supplier: ISupplier }>) {
	const [query, setQuery] = useLocationQuery({
		toQuery: filtersToUrl,
		fromQuery: filtersFromUrl,
	})
	const [params, setParams] = useSearch<TFilters>({
		visa_stage: "all",
		...query,
	})
	useEffect(() => {
		setQuery(params)
	}, [params, setQuery])

	return (
		<Box>
			<Search
				initialParams={params}
				placeholder="Search by Guest, Services etc..."
				title="Visa Bookings"
				resetParams={(params) => ({
					q: "",
					visa_stage: params.visa_stage,
				})}
				onSearch={(newParams) => {
					setParams({ ...newParams, page: 1 })
				}}
				actions={({ searchParams }) => (
					<Inline gap="2" flexWrap="wrap">
						<TemporaryStorage
							initUrl={`/visa-bookings/download`}
							initParams={{
								suppliers: [supplier.id],
								...(searchParams.visa_stage && searchParams.visa_stage !== "all"
									? { stages: [searchParams.visa_stage] }
									: {}),
							}}
						>
							{({ generate, url, message, reset, isGenerating }) =>
								url ? (
									<Button
										as="a"
										href={url}
										target="_blank"
										download
										level="secondary"
										onClick={() => reset()}
									>
										{message}
									</Button>
								) : (
									<Button
										onClick={generate}
										disabled={isGenerating}
										level="secondary"
									>
										{message || (
											<>
												<Icons.DocumentDownload /> Excel
											</>
										)}
									</Button>
								)
							}
						</TemporaryStorage>
					</Inline>
				)}
			>
				{({ searchParams, setSearchParams }) => (
					<Tabs layout="col" gap={"0"}>
						<TabsList>
							<TabItem
								active={searchParams.visa_stage === "approved"}
								onClick={() => {
									const nextParams: TFilters = {
										...searchParams,
										visa_stage: "approved",
										page: 1,
									}
									setSearchParams(nextParams)
									setParams({ ...params, ...nextParams })
								}}
							>
								Approved
							</TabItem>
							<TabItem
								active={searchParams.visa_stage === "rejected"}
								onClick={() => {
									const nextParams: TFilters = {
										...searchParams,
										visa_stage: "rejected",
										page: 1,
									}
									setSearchParams(nextParams)
									setParams({ ...params, ...nextParams })
								}}
							>
								Rejected
							</TabItem>
							<TabItem
								active={searchParams.visa_stage === "all"}
								onClick={() => {
									const nextParams: TFilters = {
										...searchParams,
										visa_stage: "all",
										page: 1,
									}
									setSearchParams(nextParams)
									setParams({ ...params, ...nextParams })
								}}
							>
								All
							</TabItem>
						</TabsList>
						<TabContent>
							<ListView<TVisaBooking & { trip: ITrip }, TFilters>
								pageKey={`supplier/${supplier.id}/visa-bookings`}
								fetch={(xhr, filters) =>
									SupplierXHR(xhr).getVisaBookings({
										...filtersToRequestParams(filters),
										suppliers: [supplier.id],
										pagination: "paginate",
									})
								}
								onPageChange={(page) => setParams({ ...params, page })}
								params={params}
							>
								{({ items }) => (
									<Table
										hover
										responsive
										headers={[
											"Guest Details",
											"Visa type",
											"Visa Duration",
											"Status & Updated By",
											"Comments",
											"Price",
										]}
										bordered
										alignCols={{
											6: "right",
										}}
										rows={items.map((booking) => {
											const {
												trip_id,
												given_price,
												comments,
												visa_type,
												latest_booking_stage,
												tourists,
											} = booking
											const tourist = tourists?.[0]

											return [
												<Stack>
													<Text fontWeight="semibold" color="accent">
														{tourist ? tourist.name : "N/A"}
													</Text>
													<Box>
														<Link
															to={`/trips/${trip_id}/services-bookings/hotels`}
															anchored
															color="accent"
														>
															#{trip_id}
														</Link>
													</Box>
												</Stack>,
												<Text>
													{visa_type.type_of_visa
														? getTypeOfVisaDisplayName(visa_type.type_of_visa)
														: "-"}
												</Text>,
												<Text>
													{visa_type.duration_formatted
														? visa_type.duration_formatted
														: "-"}
												</Text>,
												<Stack>
													<Text>{latest_booking_stage.title}</Text>
													<Text color="muted">
														{latest_booking_stage.created_at
															? utcTimestampToLocalDateString(
																	latest_booking_stage.created_at
																)
															: null}{" "}
														by{" "}
														{latest_booking_stage.created_by?.name
															? latest_booking_stage.created_by.name
															: null}
													</Text>
												</Stack>,
												<Text>{comments ?? "N/A"}</Text>,
												<Box>
													<Text fontWeight="semibold">
														Booking: <Money money={given_price} showCurrency />
													</Text>
												</Box>,
											]
										})}
									/>
								)}
							</ListView>
						</TabContent>
					</Tabs>
				)}
			</Search>
		</Box>
	)
}
